import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Button,
  Box,
  TextField,
} from '@mui/material';
import WORKSPACE_FIELDS from '../../config/workspace-fields.config';

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 420,
      width: 300,
    },
  },
};

const FieldChooser = ({
  id,
  name,
  label,
  defaultValue,
  disabled,
  sx,
  error,
  onFieldClick,
}) => {
  const selectedValue = (defaultValue || []).filter(
    (v) => v !== null && v !== undefined
  );

  const options = Object.keys(WORKSPACE_FIELDS).map((item) => ({
    label: WORKSPACE_FIELDS[item],
    value: item,
  }));

  const [tempValue, setTempValue] = useState(selectedValue);
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    setTempValue(
      (defaultValue || []).filter((v) => v !== null && v !== undefined)
    );
  }, [defaultValue]);

  const filteredOptions = options.filter((item) =>
    item.label.toLowerCase().includes(searchText.toLowerCase())
  );

  const sortedOptions = [...filteredOptions].sort((a, b) => {
    const aSelected = tempValue.includes(a.value);
    const bSelected = tempValue.includes(b.value);

    if (aSelected === bSelected) return 0;
    if (aSelected) return -1;
    return 1;
  });

  return (
    <FormControl fullWidth error={error !== ''} variant="filled">
      <InputLabel id={`field-${id}-select-label`}>{label}</InputLabel>

      <Select
        labelId={`field-${id}-select-label`}
        id={id}
        name={name}
        label={label}
        multiple
        value={tempValue}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
          setSearchText('');
        }}
        onChange={(e) => {
          const cleanValue = (e.target.value || []).filter(
            (v) => v !== null && v !== undefined
          );
          setTempValue(cleanValue);
        }}
        renderValue={(selected) =>
          options
            .filter((item) => selected?.includes(item.value))
            .map((item) => item.label)
            .join(', ')
        }
        MenuProps={MenuProps}
        disabled={disabled}
        sx={sx}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            backgroundColor: '#fff',
            zIndex: 2,
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            autoFocus
          />
        </Box>
        {sortedOptions.length > 0 ? (
          sortedOptions.map((item) => (
            <MenuItem key={`${id}-v-${item.value}`} value={item.value}>
              <Checkbox checked={tempValue.includes(item.value)} />
              <ListItemText primary={item.label} />
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No results found</MenuItem>
        )}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: '#fff',
            zIndex: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: 2,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              const finalValue = (tempValue || []).filter(
                (v) => v !== null && v !== undefined
              );

              onFieldClick(finalValue);

              setOpen(false);
              setSearchText('');
            }}
          >
            Apply
          </Button>
        </Box>
      </Select>

      {error !== '' && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

FieldChooser.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  defaultValue: PropTypes.any,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
  error: PropTypes.string,
  onFieldClick: PropTypes.func.isRequired,
};

FieldChooser.defaultProps = {
  disabled: false,
  sx: {},
  defaultValue: null,
  error: '',
};

export default FieldChooser;
